<template>
  <div class="blytz-datatable">
    <div v-if="title" class="header">
      <div class="title">{{ title }}</div>
    </div>
    <div v-if="actions.length > 0 || filters.length > 0" class="actions">
      <div class="filter-group">
        <div v-for="filter in filters" :key="filter.label">
          <div v-if="filter.type === 'date'">
            <label>Date Range</label>
            <BlytzDatePicker
              :start-date="filter.props.startDate.toString()"
              :end-date="filter.props.endDate.toString()"
              :on-date-change="filter.onFilter"
            />
          </div>
          <SwitchInput
            v-else-if="filter.type === 'toggle'"
            :label="filter.label"
            @change="filter.onFilter"
          />
        </div>
      </div>
      <div class="actions-group">
        <b-button
          v-for="action in actions"
          :key="action.label"
          :variant="action.outline ? 'outline-blue' : 'blue'"
          :disabled="action.requireSelection && selected.length === 0"
          @click="() => action.action(selected)"
        >
          {{ action.label }}
        </b-button>
      </div>
    </div>

    <b-table
      :fields="fields"
      :items="tableData.items"
      striped
      :selectable="selectable"
      :no-local-sorting="noLocalSorting"
      :fixed="fixedLayout"
      th-class="table-header-column"
      :class="className"
      @sort-changed="onSortChanged"
      @row-selected="onRowSelected"
      @row-clicked="onRowClicked"
    >
      <template #head(checkbox)="{ selectAllRows, clearSelected }">
        <input
          type="checkbox"
          :checked="allRowsSelected"
          @click="() => (allRowsSelected ? clearSelected() : selectAllRows())"
        />
      </template>
      <template #cell(checkbox)="{ rowSelected, selectRow, unselectRow }">
        <input
          type="checkbox"
          :checked="rowSelected"
          @click="rowSelected ? unselectRow() : selectRow()"
        />
      </template>

      <template v-if="onShowDetails" #cell(details)="{ item }">
        <button class="info-button" @click="() => onShowDetails(item)">
          <InfoIcon />
        </button>
      </template>

      <template v-for="(_, slotName) of $scopedSlots" #[slotName]="scope">
        <slot :name="slotName" v-bind="scope" />
      </template>
    </b-table>

    <DataTablePagination
      v-if="paginated"
      :on-page-size-change="onPageSizeChange"
      :on-page-change="onPageChange"
      :total-records="totalRecords"
      :page-size="pageSize"
      :current-page="currentPage"
      :page-size-options="pageSizeOptions"
    />
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import BlytzDatePicker from '@/components/BlytzDatePicker/BlytzDatePicker.vue'
import SwitchInput from '@/components/SwitchInput/SwitchInput.vue'
import DataTablePagination from '@/components/DataTable/DataTablePagination.vue'
import InfoIcon from '@/Icons/InfoIcon.vue'

export interface Action {
  label: string
  outline: boolean
  action: (selected: any) => void
  requireSelection: boolean
}

export interface TableData {
  headers: { key: string; label: string }[]
  items: { [key: string]: string }[]
}

export interface Filter {
  label: string
  type: 'date' | 'toggle'
  props?: any
  onFilter: (value: string) => void
}

export default Vue.extend({
  name: 'DataTable',
  components: {
    DataTablePagination,
    InfoIcon,
    BlytzDatePicker,
    SwitchInput
  },
  props: {
    selectable: {
      type: Boolean,
      default: false
    },
    showDetails: {
      type: Boolean,
      default: false
    },
    onShowDetails: {
      type: Function,
      default: () => false
    },
    title: {
      type: String,
      default: ''
    },
    actions: {
      type: Array as () => Action[],
      default: (selected: any) => [] as Action[]
    },
    onSortChanged: {
      type: Function,
      default: () => false
    },
    // b-table only ever sees the page it was handed, so its own sort reorders
    // that page and nothing else. Callers that paginate a full client-side list
    // set this and sort the list themselves in onSortChanged.
    noLocalSorting: {
      type: Boolean,
      default: false
    },
    // Auto layout sizes each column to whatever the current rows hold, so a filter that changes the
    // content also moves every column. Callers whose rows change under a filter set this and give their
    // headers explicit widths via `thStyle`.
    fixedLayout: {
      type: Boolean,
      default: false
    },
    onRowClicked: {
      type: Function,
      required: false,
      default: undefined
    },
    filters: {
      type: Array as () => Filter[],
      default: () => [] as Filter[]
    },
    hasDateFilter: {
      type: Boolean,
      default: false
    },
    onDateFilter: {
      type: Function,
      default: (dateRange: string) => false
    },
    tableData: {
      type: Object as () => TableData,
      default: () => ({ headers: [], items: [] })
    },
    onPageChange: {
      type: Function,
      default: (page: number) => false
    },
    onPageSizeChange: {
      type: Function,
      default: (size: number) => false
    },
    currentPage: {
      type: Number,
      default: 1
    },
    pageSize: {
      type: Number,
      default: 10
    },
    pageSizeOptions: {
      type: Array,
      default: () => [10, 20, 50]
    },
    totalRecords: {
      type: Number,
      default: 0
    },
    paginated: {
      type: Boolean,
      default: true
    },
    className: {
      type: String,
      default: ''
    }
  },
  data(): {
    selected: any[]
    startDate: Date
    endDate: Date
  } {
    return {
      selected: [] as any[],
      // start date 7 days ago. End date today
      startDate: new Date(new Date().setDate(new Date().getDate() - 7)),
      endDate: new Date()
    }
  },
  computed: {
    fields(): { key: string; label: string }[] {
      const { headers } = this.tableData as TableData
      this.showDetails && headers.push({ key: 'details', label: 'Details' })
      this.selectable && headers.unshift({ key: 'checkbox', label: '' })

      return headers
    },
    allRowsSelected(): boolean {
      return (
        (this.selected as []).length ===
        (this.tableData as TableData).items.length
      )
    },
    isClickable(): boolean {
      return typeof this.onRowClicked === 'function'
    }
  },
  methods: {
    onRowSelected(items: any[]) {
      this.$data.selected = items
    },
    dateChange(e) {
      console.log('change', e.target.checked)
    }
  }
})
</script>

<style lang="scss">
/* This CSS should be scoped, but we are not using scoped styles because of a problem that prevents styling table headers */
.blytz-datatable {
  margin: 24px 0;

  border-radius: 8px;
  thead > tr > th {
    vertical-align: middle;
  }
  .header {
    display: flex;
    justify-content: space-between;
    padding: 12px;
  }
  .title {
    margin-bottom: 1rem;

    color: #5f3effbf;
  }
  .info-button {
    background: none;
    border: none;
  }
  .actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 1rem;
  }
  .filters {
    display: flex;
    gap: 16px;
    align-items: center;
    padding: 0 12px 12px;
  }
  .actions-group {
    display: flex;
    gap: 0.5rem;
    justify-content: flex-end;
    height: fit-content;
  }
}
</style>
